import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, MODELS, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, FileType2, FileText, Loader2, Sparkles, Printer, Image as ImageIcon, Lightbulb, ShieldCheck, ScanText } from "lucide-react";
import { motion } from "framer-motion";
import RichEditor from "@/components/RichEditor";
// Voice-to-draft removed per product decision.
import AIFullProject from "@/components/AIFullProject";

export default function ThesisBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [templates, setTemplates] = useState({});
  const [activeKey, setActiveKey] = useState("chapter_1_introduction");
  const [saving, setSaving] = useState(false);
  const [aiInstr, setAiInstr] = useState("");
  const [aiModel, setAiModel] = useState(0);
  const [aiStreaming, setAiStreaming] = useState(false);
  const [aiPreview, setAiPreview] = useState("");
  const [assetCap, setAssetCap] = useState(""); const [assetCtx, setAssetCtx] = useState("");
  const [assetKind, setAssetKind] = useState("figure"); const [assetResult, setAssetResult] = useState(null);
  const [assetBusy, setAssetBusy] = useState(false);
  const [spinRes, setSpinRes] = useState(null); const [spinBusy, setSpinBusy] = useState(false);
  const [consBusy, setConsBusy] = useState(false); const [consRes, setConsRes] = useState(null); const [consErr, setConsErr] = useState("");
  const fileRef = useRef(null);

  const runConsistency = async () => {
    setConsBusy(true); setConsRes(null); setConsErr("");
    const tid = toast.loading("Scanning chapters for contradictions & terminology drift…");
    try {
      const m = MODELS[aiModel];
      const { data } = await api.post(`/thesis/${id}/consistency`, { model_provider: m.provider, model_name: m.name });
      setConsRes(data); toast.dismiss(tid); toast.success(`${(data.issues||[]).length} issues flagged`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Scan failed — please try again.";
      setConsErr(msg); toast.dismiss(tid); toast.error(msg);
    }
    finally { setConsBusy(false); }
  };

  useEffect(() => {
    api.get(`/thesis/${id}`).then(r => setT(r.data)).catch(()=>{ toast.error("Not found"); nav("/app/thesis"); });
    api.get("/thesis/templates").then(r => setTemplates(r.data.templates || {}));
  }, [id, nav]);

  const setSec = (k, v) => setT(prev => ({ ...prev, sections: { ...prev.sections, [k]: v } }));

  const applyFullProject = ({ suggested_title, sections }) => {
    setT(prev => {
      const merged = { ...(prev.sections || {}) };
      Object.entries(sections || {}).forEach(([k, html]) => { if (html) merged[k] = html; });
      return {
        ...prev,
        title: (!prev.title || prev.title === "Untitled thesis") ? (suggested_title || prev.title) : prev.title,
        sections: merged,
      };
    });
  };

  const save = async (silent=false) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/thesis/${id}`, { title:t.title, template_key:t.template_key, university:t.university, supervisor:t.supervisor, sections:t.sections, references:t.references, assets:t.assets });
      setT(data); if (!silent) toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  useEffect(() => { if (!t) return; const to = setTimeout(()=>save(true), 1500); return ()=>clearTimeout(to); /* eslint-disable-line */ }, [t?.title, t?.template_key, t?.sections, t?.references, t?.assets]);

  const runAI = async () => {
    const m = MODELS[aiModel]; setAiStreaming(true); setAiPreview("");
    const token = localStorage.getItem("gf_token");
    try {
      const res = await fetch(`${API}/thesis/ai/generate`, {
        method:"POST", headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
        body: JSON.stringify({ thesis_id:id, section_key:activeKey, model_provider:m.provider, model_name:m.name, instruction:aiInstr }),
      });
      if (!res.ok) throw new Error("AI failed");
      const rd = res.body.getReader(); const dec = new TextDecoder(); let buf="", acc="";
      while (true) { const {done,value}=await rd.read(); if (done) break; buf+=dec.decode(value,{stream:true}); const parts=buf.split("\n\n"); buf=parts.pop()||"";
        for (const part of parts) { const line=part.replace(/^data:\s*/,"").trim(); if (!line) continue;
          try { const o=JSON.parse(line); if (o.delta){acc+=o.delta; setAiPreview(acc);} if (o.error) toast.error(o.error); } catch {}
      } }
    } catch (e) { toast.error(e.message||"AI failed"); }
    finally { setAiStreaming(false); }
  };
  const acceptAI = () => { setSec(activeKey, (t.sections?.[activeKey] || "") + mdToHtml(aiPreview)); setAiPreview(""); setAiInstr(""); toast.success("Inserted"); };

  const exportF = async (kind) => {
    const res = await fetch(`${API}/thesis/${id}/export/${kind}`, { headers:{Authorization:`Bearer ${localStorage.getItem("gf_token")}`}});
    if (!res.ok) return toast.error("Export failed");
    const blob = await res.blob(); const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download=`${t.title||"thesis"}.${kind}`; a.click();
  };

  const uploadAsset = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const rd = new FileReader();
    rd.onload = () => {
      const dataUrl = rd.result;
      const asset = { id: crypto.randomUUID(), kind: assetKind, caption: assetCap, dataUrl };
      setT(prev => ({ ...prev, assets: [...(prev.assets||[]), asset] }));
      // Insert into current section
      const tag = assetKind === "table" ? "" : `<figure><img src="${dataUrl}" alt="${assetCap}"/><figcaption>${assetCap}</figcaption></figure>`;
      setSec(activeKey, (t.sections?.[activeKey] || "") + tag);
      toast.success("Asset inserted");
    };
    rd.readAsDataURL(f);
  };

  const describeAsset = async () => {
    const m = MODELS[aiModel]; setAssetBusy(true); setAssetResult(null);
    try {
      const { data } = await api.post("/thesis/asset/describe", { thesis_id:id, asset_type:assetKind, caption:assetCap, context:assetCtx, model_provider:m.provider, model_name:m.name });
      setAssetResult(data);
    } catch { toast.error("Could not describe asset"); }
    finally { setAssetBusy(false); }
  };
  const insertAssetProse = () => {
    if (!assetResult?.paragraph) return;
    const p = `<p>${assetResult.paragraph}</p>`;
    setSec(activeKey, (t.sections?.[activeKey] || "") + p);
    toast.success("Prose inserted"); setAssetResult(null);
  };

  const spinoff = async (kind) => {
    const m = MODELS[aiModel]; setSpinBusy(true); setSpinRes(null);
    try {
      const { data } = await api.post("/thesis/spinoff", { thesis_id:id, kind, section_key:activeKey, model_provider:m.provider, model_name:m.name });
      setSpinRes({ kind, data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Spin-off failed"); }
    finally { setSpinBusy(false); }
  };

  if (!t) return <div className="p-10 text-muted-foreground">Loading…</div>;
  const keys = templates[t.template_key] || Object.keys(t.sections || {});

  return (
    <div className="min-h-screen paper">
      <div className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="max-w-[1500px] mx-auto px-6 py-3 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={()=>nav("/app/thesis")}><ArrowLeft className="w-4 h-4 mr-1"/>Theses</Button>
          <Input value={t.title} onChange={(e)=>setT({...t, title:e.target.value})} className="flex-1 font-display text-xl border-none shadow-none focus-visible:ring-0 bg-transparent" data-testid="thesis-title"/>
          <div className="flex gap-2">
            {saving ? <span className="text-xs text-muted-foreground"><Loader2 className="inline w-3 h-3 mr-1 animate-spin"/>Saving…</span> : <span className="text-xs text-muted-foreground">Saved</span>}
            <AIFullProject module="thesis" title={t.title} testidPrefix="thesis-fp" docId={id} saveDoc={()=>save(true)} buttonLabel="Generate full thesis" dialogTitle="Generate full thesis from an idea" sectionKeys={templates[t.template_key] || []} sectionLabels={Object.fromEntries((templates[t.template_key] || []).map(k => [k, k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())]))} onApply={applyFullProject}/>
            <Button size="sm" variant="outline" onClick={()=>save(false)}><Save className="w-4 h-4 mr-1"/>Save</Button>
            <Button size="sm" onClick={()=>exportF("pdf")} className="rounded-full" data-testid="thesis-pdf"><FileType2 className="w-4 h-4 mr-1"/>PDF</Button>
            <Button size="sm" variant="outline" onClick={()=>exportF("docx")} data-testid="thesis-docx"><FileText className="w-4 h-4 mr-1"/>DOCX</Button>
            <Button size="sm" variant="outline" onClick={()=>window.print()}><Printer className="w-4 h-4 mr-1"/>Print</Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 py-6 grid grid-cols-12 gap-6">
        <aside className="col-span-12 lg:col-span-3">
          <div className="sticky top-24 space-y-4">
            <div className="card-crisp rounded-lg p-4">
              <div className="overline mb-2">Format</div>
              <Select value={t.template_key} onValueChange={(v)=>setT({...t, template_key:v})}>
                <SelectTrigger data-testid="thesis-template-select"><SelectValue/></SelectTrigger>
                <SelectContent>{Object.keys(templates).map(k=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
              <div className="mt-3 grid grid-cols-1 gap-2">
                <div><Label className="text-xs">University</Label><Input value={t.university || ""} onChange={(e)=>setT({...t, university:e.target.value})}/></div>
                <div><Label className="text-xs">Supervisor</Label><Input value={t.supervisor || ""} onChange={(e)=>setT({...t, supervisor:e.target.value})}/></div>
              </div>
            </div>
            <div className="card-crisp rounded-lg p-3">
              <div className="overline mb-2 px-1">Outline</div>
              <ul className="space-y-0.5 max-h-[420px] overflow-auto">
                {keys.map(k => {
                  const filled = ((t.sections?.[k] || "").replace(/<[^>]+>/g," ").trim()).length > 0;
                  const active = activeKey === k;
                  return (<li key={k}><button onClick={()=>setActiveKey(k)} className={`w-full text-left px-2 py-1.5 rounded-md text-[13px] flex items-center justify-between ${active?"bg-primary/10 text-primary":"hover:bg-secondary"}`} data-testid={`thesis-outline-${k}`}><span className="truncate">{k.replace(/_/g," ").replace(/\b\w/g, c=>c.toUpperCase())}</span><span className={`w-1.5 h-1.5 rounded-full ${filled?"bg-primary":"bg-muted-foreground/30"}`}/></button></li>);
                })}
              </ul>
            </div>
          </div>
        </aside>

        <section className="col-span-12 lg:col-span-6">
          <motion.div key={activeKey} initial={{opacity:0, y:6}} animate={{opacity:1, y:0}} transition={{duration:0.2}}>
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="overline">Section</div>
                <h2 className="font-display text-3xl capitalize">{activeKey.replace(/_/g," ")}</h2>
              </div>
              <div className="text-xs text-muted-foreground">
                {(t.sections?.[activeKey] || "").replace(/<[^>]+>/g," ").trim().split(/\s+/).filter(Boolean).length} words
              </div>
            </div>
            <RichEditor value={t.sections?.[activeKey] || ""} onChange={(html)=>setSec(activeKey, html)} placeholder="Draft this section, or invite the AI writer on the right."/>
          </motion.div>
        </section>

        <aside className="col-span-12 lg:col-span-3">
          <div className="sticky top-24 space-y-4">
            <div className="card-crisp rounded-lg p-4">
              <div className="font-display text-xl flex items-center gap-2 mb-3"><Sparkles className="w-5 h-5 text-primary"/>AI drafter</div>
              <div><Label className="text-xs">Model</Label>
                <Select value={String(aiModel)} onValueChange={(v)=>setAiModel(Number(v))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="mt-2"><Label className="text-xs">Instruction</Label><Textarea value={aiInstr} onChange={(e)=>setAiInstr(e.target.value)} placeholder="e.g., include recent literature 2020-2025" className="min-h-[60px]"/></div>
              <div className="flex gap-2 mt-2">
                <Button className="flex-1 rounded-full" onClick={runAI} disabled={aiStreaming} data-testid="thesis-ai-run">{aiStreaming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Drafting…</> : <><Sparkles className="w-4 h-4 mr-2"/>Draft</>}</Button>
                {/* Voice-to-draft removed */}
              </div>
              {aiPreview && <div className="border border-primary/40 rounded-md p-3 bg-primary/5 max-h-[240px] overflow-auto whitespace-pre-wrap text-sm mt-2">{aiPreview}</div>}
              {aiPreview && !aiStreaming && <div className="flex gap-2 mt-2"><Button className="flex-1 rounded-full" onClick={acceptAI}>Insert</Button><Button variant="outline" onClick={()=>setAiPreview("")}>Discard</Button></div>}
            </div>

            <div className="card-crisp rounded-lg p-4">
              <div className="font-display text-lg flex items-center gap-2 mb-2"><ImageIcon className="w-5 h-5 text-primary"/>Insert asset</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Select value={assetKind} onValueChange={setAssetKind}><SelectTrigger className="h-8 text-xs"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="figure">Figure</SelectItem><SelectItem value="bar_chart">Bar chart</SelectItem><SelectItem value="table">Table (image)</SelectItem></SelectContent></Select>
                <Input placeholder="Caption" value={assetCap} onChange={(e)=>setAssetCap(e.target.value)} className="h-8 text-xs"/>
              </div>
              <Textarea placeholder="What does it show? (context)" value={assetCtx} onChange={(e)=>setAssetCtx(e.target.value)} className="min-h-[50px] text-xs"/>
              <div className="flex gap-2 mt-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadAsset}/>
                <Button size="sm" variant="outline" onClick={()=>fileRef.current?.click()} className="flex-1"><ImageIcon className="w-4 h-4 mr-1"/>Upload &amp; embed</Button>
                <Button size="sm" onClick={describeAsset} disabled={assetBusy} className="rounded-full"><Sparkles className="w-4 h-4 mr-1"/>{assetBusy?"…":"AI describe"}</Button>
              </div>
              {assetResult && (
                <div className="mt-2 border border-primary/40 rounded-md p-2 bg-primary/5 text-xs">
                  <div className="font-medium mb-1">{assetResult.caption_final}</div>
                  <div className="text-muted-foreground line-clamp-6">{assetResult.paragraph}</div>
                  <Button size="sm" className="w-full mt-2 rounded-full" onClick={insertAssetProse}>Insert prose</Button>
                </div>
              )}
            </div>

            <div className="card-crisp rounded-lg p-4">
              <div className="font-display text-lg flex items-center gap-2 mb-2"><ScanText className="w-5 h-5 text-primary"/>Consistency AI</div>
              <p className="text-xs text-muted-foreground mb-2">One-pass cross-chapter check for contradicting numbers, terminology drift, citation-style shifts, and undefined acronyms.</p>
              <Button size="sm" className="w-full rounded-full" onClick={runConsistency} disabled={consBusy} data-testid="thesis-consistency-run">{consBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Scanning…</> : <><ScanText className="w-4 h-4 mr-2"/>Scan whole thesis</>}</Button>
              {consErr && !consBusy && (
                <div className="mt-2 border border-red-300 bg-red-50 rounded-md p-2 text-xs text-red-800" data-testid="thesis-consistency-error">{consErr}</div>
              )}
              {consRes && (
                <div className="mt-3 border border-primary/40 rounded-md p-3 bg-primary/5 text-xs max-h-[360px] overflow-auto" data-testid="thesis-consistency-result">
                  {consRes.one_line_verdict && <div className="font-medium italic mb-2">"{consRes.one_line_verdict}"</div>}
                  {(consRes.top_priorities || []).length>0 && (
                    <div className="mb-2"><b>Top priorities</b><ul className="list-disc pl-4 mt-0.5">{consRes.top_priorities.map((p,i)=><li key={i}>{p}</li>)}</ul></div>
                  )}
                  {(consRes.issues || []).map((iss,i)=>(
                    <div key={i} className="border-t border-primary/20 pt-2 mt-2">
                      <div className="flex items-center gap-1 flex-wrap"><span className={`px-1.5 rounded-full text-[10px] ${iss.severity==='high'?'bg-red-200 text-red-900':iss.severity==='med'?'bg-amber-200 text-amber-900':'bg-secondary'}`}>{iss.severity}</span><span className="uppercase tracking-widest text-[9px] text-primary">{iss.category}</span></div>
                      <div className="font-medium mt-1">{iss.description}</div>
                      <div className="text-[10px] text-muted-foreground">Chapters: {(iss.chapters||[]).join(", ")}</div>
                      {(iss.evidence||[]).length>0 && <ul className="mt-1 list-disc pl-4 text-[11px]">{iss.evidence.map((e,j)=><li key={j}>{e}</li>)}</ul>}
                      {iss.fix && <div className="mt-1 italic text-[11px]">→ {iss.fix}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card-crisp rounded-lg p-4">
              <div className="font-display text-lg flex items-center gap-2 mb-2"><Lightbulb className="w-5 h-5 text-primary"/>Spin-offs</div>
              <div className="grid grid-cols-1 gap-2">
                <Button size="sm" variant="outline" onClick={()=>spinoff("paper_from_chapter")} disabled={spinBusy} data-testid="spin-paper">Generate paper from this chapter</Button>
                <Button size="sm" variant="outline" onClick={()=>spinoff("research_topics")} disabled={spinBusy} data-testid="spin-topics">Suggest new research topics</Button>
                <a href="/app/plagiarism" className="w-full"><Button size="sm" className="w-full" variant="outline"><ShieldCheck className="w-4 h-4 mr-1"/>Run originality check</Button></a>
              </div>
              {spinRes && (
                <div className="mt-3 border border-primary/40 rounded-md p-3 bg-primary/5 text-xs max-h-[280px] overflow-auto">
                  {spinRes.kind === "paper_from_chapter" ? (
                    <>
                      <div className="font-display text-sm">{spinRes.data.title}</div>
                      <div className="mt-1"><b>Abstract:</b> {spinRes.data.abstract}</div>
                      <div className="mt-1"><b>Target journals:</b> {(spinRes.data.target_journals||[]).join(", ")}</div>
                    </>
                  ) : (
                    <ol className="list-decimal pl-4 space-y-2">
                      {(spinRes.data.topics||[]).map((tp,i)=>(
                        <li key={i}><b>{tp.title}</b><div className="text-muted-foreground">{tp.rationale}</div></li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function mdToHtml(md){
  if (!md) return "";
  const esc = s=>s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
  const inline = s=>s.replace(/\*\*(.+?)\*\*/g,"<strong>$1</strong>").replace(/\*(.+?)\*/g,"<em>$1</em>").replace(/`([^`]+)`/g,"<code>$1</code>");
  const lines=md.split(/\r?\n/); const out=[]; let inList=null;
  const flush=()=>{ if(inList){out.push(`</${inList}>`); inList=null;} };
  for (const line of lines) {
    if(/^\s*#{3}\s+/.test(line)){flush(); out.push(`<h3>${esc(line.replace(/^\s*###\s+/,""))}</h3>`); continue;}
    if(/^\s*#{2}\s+/.test(line)){flush(); out.push(`<h2>${esc(line.replace(/^\s*##\s+/,""))}</h2>`); continue;}
    if(/^\s*#\s+/.test(line)){flush(); out.push(`<h1>${esc(line.replace(/^\s*#\s+/,""))}</h1>`); continue;}
    if(/^\s*[-*]\s+/.test(line)){if(inList!=="ul"){flush(); out.push("<ul>"); inList="ul";} out.push(`<li>${inline(esc(line.replace(/^\s*[-*]\s+/,"")))}</li>`); continue;}
    if(/^\s*\d+\.\s+/.test(line)){if(inList!=="ol"){flush(); out.push("<ol>"); inList="ol";} out.push(`<li>${inline(esc(line.replace(/^\s*\d+\.\s+/,"")))}</li>`); continue;}
    if(line.trim()===""){flush(); continue;}
    flush(); out.push(`<p>${inline(esc(line))}</p>`);
  }
  flush(); return out.join("\n");
}
